import { html } from "lit";
import { queryAssignedElements, state } from "lit/decorators.js";
import { createRef, ref } from "lit-html/directives/ref.js";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../../atom/Column/index.js";
import "../../atom/Icon/index.js";
import "../../atom/UploadProgress/index.js";
import "../../atom/Row/index.js";
import "../../atom/Text/index.js";
import "../Link/index.js";
import "../Tooltip/index.js";
import { prop } from "../../../decorators/prop/index.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { BaseComponent } from "../../BaseComponent.js";
import { rif } from "../../../helpers/renderIf.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfMiniUploader = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.dsf_multiple = false;
    this.dsf_state = "default";
    this.dsf_upload_progress = "indeterminate";
    this._inputRef = createRef();
    this._hasInfoTooltip = false;
    this._hasErrorTooltip = false;
  }
  onUploadClick() {
    var _a;
    (_a = this._inputRef.value) == null ? void 0 : _a.click();
  }
  onFileChange(e) {
    const {
      currentTarget: { files }
    } = e;
    const fileList = files ? [...files] : [];
    this.emit({ name: "files", payload: fileList });
  }
  get isUploading() {
    return this.dsf_state === "uploading";
  }
  handleInfoTooltipSlotChange() {
    this._hasInfoTooltip = this.infoTooltipSlot && this.infoTooltipSlot.length > 0 || false;
  }
  renderInfoComponent() {
    const infoMessageComponent = html`
      <div class="flex items-center" slot="anchor">
        <dsf-text dsf_level="xxs" dsf_color_intensity="500">${this.dsf_message}</dsf-text>
      </div>
      <slot name="info-tooltip" @slotchange=${this.handleInfoTooltipSlotChange}></slot>
    `;
    if (this._hasInfoTooltip) {
      return html`
        <dsf-tooltip
          dsf_placement="bottom"
          dsf_size="small"
          dsf_type="outlined"
          exportparts="dropdown: info-tooltip-dropdown"
        >
          ${infoMessageComponent}
        </dsf-tooltip>
      `;
    }
    return infoMessageComponent;
  }
  handleErrorTooltipSlotChange() {
    this._hasErrorTooltip = this.errorTooltipSlot && this.errorTooltipSlot.length > 0 || false;
  }
  renderErrorComponent() {
    const errorMessageComponent = html`
      <div class="flex items-center" slot="anchor">
        <dsf-text dsf_level="xxs" dsf_color="error" dsf_color_intensity="300"
          >${this.dsf_error_message}</dsf-text
        >
      </div>
      <slot name="error-tooltip" @slotchange=${this.handleErrorTooltipSlotChange}></slot>
    `;
    if (this._hasErrorTooltip) {
      return html`
        <dsf-tooltip
          dsf_placement="bottom"
          dsf_size="small"
          dsf_type="outlined"
          exportparts="dropdown: error-tooltip-dropdown"
        >
          ${errorMessageComponent}
        </dsf-tooltip>
      `;
    }
    return errorMessageComponent;
  }
  renderDefaultState() {
    return html`
      <dsf-link
        dsf_icon="Paperclip"
        dsf_size="medium"
        @click=${this.onUploadClick}
        ?disabled=${this.disabled || this.isUploading}
      >
        ${this.dsf_label}
      </dsf-link>
      <input
        @change=${this.onFileChange}
        accept=${ifDefined(this.dsf_accepted_mime_types)}
        .multiple=${this.dsf_multiple}
        style="display: none"
        type="file"
        ${ref(this._inputRef)}
      />
    `;
  }
  renderUploadingState() {
    return html`
      <dsf-upload-progress
        dsf_upload_progress=${ifDefined(this.dsf_upload_progress)}
        class="mt-2"
      ></dsf-upload-progress>
      <dsf-text dsf_level="xs">${this.dsf_label}</dsf-text>
    `;
  }
  renderSuccessState() {
    return html`
      <dsf-icon
        dsf_name="Check"
        dsf_size="14px"
        class="mt-2 rounded-full bg-secondary-300 p-2 text-neutral-0"
      ></dsf-icon>
      <dsf-text dsf_level="xs">${this.dsf_label}</dsf-text>
    `;
  }
  render() {
    return html`
      <dsf-col class="gap-4">
        <dsf-row class="gap-6">
          ${rif(this.isUploading, this.renderUploadingState())}
          ${rif(this.dsf_state === "success", this.renderSuccessState())}
          ${rif(this.dsf_state === "default", this.renderDefaultState())}
        </dsf-row>
        <div class="flex gap-4 pl-20">
          ${rif(this.dsf_message, this.renderInfoComponent())}
          ${rif(this.dsf_error_message, this.renderErrorComponent())}
        </div>
      </dsf-col>
    `;
  }
};
__decorateClass([
  prop({ type: String })
], DsfMiniUploader.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfMiniUploader.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfMiniUploader.prototype, "dsf_error_message", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfMiniUploader.prototype, "dsf_multiple", 2);
__decorateClass([
  prop({ type: String })
], DsfMiniUploader.prototype, "dsf_accepted_mime_types", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfMiniUploader.prototype, "dsf_state", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfMiniUploader.prototype, "dsf_upload_progress", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfMiniUploader.prototype, "disabled", 2);
__decorateClass([
  queryAssignedElements({ flatten: true, slot: "info-tooltip" })
], DsfMiniUploader.prototype, "infoTooltipSlot", 2);
__decorateClass([
  queryAssignedElements({ flatten: true, slot: "error-tooltip" })
], DsfMiniUploader.prototype, "errorTooltipSlot", 2);
__decorateClass([
  preventDefault
], DsfMiniUploader.prototype, "onUploadClick", 1);
__decorateClass([
  state()
], DsfMiniUploader.prototype, "_hasInfoTooltip", 2);
__decorateClass([
  state()
], DsfMiniUploader.prototype, "_hasErrorTooltip", 2);
DsfMiniUploader = __decorateClass([
  defineElement("dsf-mini-uploader")
], DsfMiniUploader);
export {
  DsfMiniUploader
};
